import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import Switch from "../../../components/toggleButton02";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";
import Tooltip from "@mui/material/Tooltip";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { showSuccessAlert, showErrorAlert } from "../../uiInterface/base-ui/alertservice";
import { useTenantRole } from "../../../components/TenantRoleDisplay";

const EmailWorkflow = () => {
  const route = all_routes;
  const { selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const isSalesUser = userRole?.role_key === "SALESUSER";
  const [moveToPipeline, setMoveToPipeline] = useState(true);
  const [deactivateEmailsCount, setDeactivateEmailsCount] = useState("3");
  const [originalMoveToPipeline, setOriginalMoveToPipeline] = useState(true);
  const [originalDeactivateEmailsCount, setOriginalDeactivateEmailsCount] = useState("3");
  const [crmWorkflowId1, setCrmWorkflowId1] = useState<number | null>(null);
  const [crmWorkflowId2, setCrmWorkflowId2] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!selectedTenantId) return;

    const authHeader = localStorage.getItem("authCred");
    if (!authHeader) return;

    const fetchConfig = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/crm-workflow/?tenant_id=${selectedTenantId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        });
        const data = await res.json();
        
        if (data && data.results) {
          const conf1 = data.results.find((item: any) => item.workflow_type === "lead_to_pipeline_on_first_email");
          if (conf1) {
            setMoveToPipeline(conf1.config_json?.is_active ?? true);
            setOriginalMoveToPipeline(conf1.config_json?.is_active ?? true);
            setCrmWorkflowId1(conf1.crm_workflow_id);
          }
          
          const conf2 = data.results.find((item: any) => item.workflow_type === "inactivity_based_on_email_activity");
          if (conf2) {
            setDeactivateEmailsCount(conf2.config_json?.number_of_emails?.toString() ?? "3");
            setOriginalDeactivateEmailsCount(conf2.config_json?.number_of_emails?.toString() ?? "3");
            setCrmWorkflowId2(conf2.crm_workflow_id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch workflow configuration", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchConfig();
  }, [selectedTenantId]);

  const save = async () => {
    setIsSaving(true);
    const authHeader = localStorage.getItem("authCred");
    let userId = null;
    
    const authResponse = localStorage.getItem("authResponse");
    if (authResponse) {
      try {
        const authData = JSON.parse(authResponse);
        userId = authData.user_id || authData.user?.user_id || null;
      } catch (e) {
        // ignore
      }
    }
    
    if (!authHeader || !selectedTenantId) {
      await showErrorAlert("Missing required authentication or IDs.");
      return;
    }

    try {
      // Save Setting 1
      const payload1 = {
        tenant_id: parseInt(selectedTenantId),
        config_name: "lead_to_pipeline_config",
        workflow_type: "lead_to_pipeline_on_first_email",
        is_enabled: true,
        config_json: {
          is_active: moveToPipeline
        },
        created_by: userId,
        updated_by: userId
      };

      let res1;
      if (crmWorkflowId1) {
        res1 = await fetch(`${API_BASE_URL}/crm-workflow/${crmWorkflowId1}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(payload1),
        });
      } else {
        res1 = await fetch(`${API_BASE_URL}/crm-workflow/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(payload1),
        });
      }

      if (!res1.ok) {
        throw new Error("Failed to save lead_to_pipeline config");
      }
      const data1 = await res1.json();
      if (!crmWorkflowId1 && data1.crm_workflow_id) {
        setCrmWorkflowId1(data1.crm_workflow_id);
      }

      // Save Setting 2
      const payload2 = {
        tenant_id: parseInt(selectedTenantId),
        config_name: "email_inactivity_config",
        workflow_type: "inactivity_based_on_email_activity",
        is_enabled: true,
        config_json: {
          is_active: true,
          number_of_emails: parseInt(deactivateEmailsCount)
        },
        created_by: userId,
        updated_by: userId
      };

      let res2;
      if (crmWorkflowId2) {
        res2 = await fetch(`${API_BASE_URL}/crm-workflow/${crmWorkflowId2}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(payload2),
        });
      } else {
        res2 = await fetch(`${API_BASE_URL}/crm-workflow/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(payload2),
        });
      }

      if (!res2.ok) {
        throw new Error("Failed to save inactivity config");
      }
      const data2 = await res2.json();
      if (!crmWorkflowId2 && data2.crm_workflow_id) {
        setCrmWorkflowId2(data2.crm_workflow_id);
      }

      setOriginalMoveToPipeline(moveToPipeline);
      setOriginalDeactivateEmailsCount(deactivateEmailsCount);
      await showSuccessAlert("Workflow configuration saved successfully.");
    } catch (err) {
      console.error(err);
      await showErrorAlert("An error occurred while saving the configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = () => {
    setMoveToPipeline(originalMoveToPipeline);
    setDeactivateEmailsCount(originalDeactivateEmailsCount);
  };

  const hasChanged = moveToPipeline !== originalMoveToPipeline || deactivateEmailsCount !== originalDeactivateEmailsCount;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">

            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>



            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="workflow-configuration"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>


            <div className="row">
              <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                <div className="card">
                  <div className="card-body">
                    <div className="settings-sidebar">
                      <h4 className="fw-semibold mb-3">Workflow Configuration</h4>
                      <div className="list-group list-group-flush settings-sidebar">
                        <Link
                          to={route.workflowConfiguration}
                          className="fw-medium active"
                        >
                          Email Workflow
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    {isLoading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="border-bottom mb-3 pb-3">
                          <h4 className="fw-semibold mb-1">
                            Email Workflow Settings
                          </h4>
                          <p>
                            Configure the email workflow settings below
                          </p>
                        </div>


                    <div className="mb-4 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <label className="form-label fw-medium mb-0 me-2">
                          Move {getLifecycleTypeName('LEAD')} to {getLifecycleTypeName('PIPELINE')} after sending first email
                        </label>
                        <Tooltip title={`Automatically transfer ${getLifecycleTypeName('LEAD')} to the ${getLifecycleTypeName('PIPELINE')} stage once the initial contact email is sent.`} placement="top" arrow>
                          <i
                            className="ti ti-info-circle text-muted"
                            style={{ cursor: "pointer", fontSize: "16px" }}
                          ></i>
                        </Tooltip>
                      </div>
                      <div className="ms-3">
                        <Switch
                          checked={moveToPipeline}
                          onChange={(e) => setMoveToPipeline(e.target.checked)}
                          disabled={isSalesUser}
                        />
                      </div>
                    </div>

                    <div className="mb-4 d-flex align-items-center justify-content-between">
                      <div>
                        <label className="form-label fw-medium mb-0">
                          Deactivate {getLifecycleTypeName('LEAD')} after how many emails without response?
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          value={deactivateEmailsCount}
                          onChange={(e) =>
                            setDeactivateEmailsCount(e.target.value)
                          }
                          placeholder="e.g. 3"
                          min="1"
                          disabled={isSalesUser}
                        />
                      </div>
                    </div>

                        {hasChanged && !isSalesUser && (
                          <div className="mt-4">
                            <button
                              type="button"
                              className="btn btn-outline-secondary me-2"
                              onClick={cancel}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={save}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Saving...
                                </>
                              ) : (
                                "Save Changes"
                              )}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailWorkflow;
